import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { useOuverture } from "@/contexts/LanguageContext";
import { horairesSchemaOrg, insererPhrases } from "@shared/ouverture";

// Marqueur remplacé par les horaires réglés dans le back-office (openingHoursSpecification).
const HORAIRES = "__HORAIRES__";

const SITE_NAME = "Château de Syam — Villa Palladienne";
const BASE_URL = "https://chateaudesyam.fr";
const DEFAULT_IMAGE = `${BASE_URL}/media/Visiter_Header_TEST_694686b0_opt_3e4d6dc8.webp`;
const MARKDOWN_MAIN_PATHS = new Set([
  "/",
  "/visiter",
  "/dormir",
  "/gites",
  "/mariage",
  "/evenement",
  "/groupe",
  "/offrir",
  "/roseraie",
]);

export interface SEOProps {
  title: string;
  description: string;
  path: string;
  image?: string;
  heroSrcSet?: string;
  heroSizes?: string;
  type?: "website" | "article";
  jsonLd?: object | object[];
  noindex?: boolean;
  keywords?: string;
  /**
   * Stratégie hreflang :
   * - "home" : page d'accueil avec routes /en /de /nl séparées
   * - "minisite" : page multilingue sur la même URL (langue via LanguageContext)
   * - false (défaut) : pas de hreflang (pages légales, admin, SEO fantômes)
   */
  hreflang?: "home" | "minisite" | false;
}

function JsonLdScripts({ schemas }: { schemas: string[] }) {
  // Pré-rendu au build : les données structurées sont collectées pour être écrites dans le HTML.
  const collecteur = (globalThis as { __CHATEAU_JSON_LD__?: string[] }).__CHATEAU_JSON_LD__;
  if (collecteur) collecteur.push(...schemas);

  useEffect(() => {
    // Celles du HTML pré-rendu sont remplacées par celles de la page affichée.
    document.querySelectorAll('script[type="application/ld+json"][data-prerendu]').forEach((s) => s.remove());
    const scripts = schemas.map((schema) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.chateauJsonLd = "true";
      script.textContent = schema;
      document.head.appendChild(script);
      return script;
    });

    return () => scripts.forEach((script) => script.remove());
  }, [schemas]);

  return null;
}

export function SEO({
  title,
  description,
  path,
  image = DEFAULT_IMAGE,
  heroSrcSet,
  heroSizes,
  type = "website",
  jsonLd,
  noindex = false,
  keywords,
  hreflang = false,
}: SEOProps) {
  const url = `${BASE_URL}${path}`;
  // Open Graph / Twitter exigent une adresse absolue.
  const imageAbsolue = image.startsWith("/") ? `${BASE_URL}${image}` : image;
  const markdownUrl = MARKDOWN_MAIN_PATHS.has(path)
    ? `${BASE_URL}${path === "/" ? "/index.md" : `${path}/index.md`}`
    : undefined;
  const shouldPreloadHero = MARKDOWN_MAIN_PATHS.has(path);
  const fullTitle = title.includes("Château de Syam")
    ? title
    : `${title} — Château de Syam`;

  // Données structurées en français, avec la saison et les horaires réglés dans le back-office.
  const ouverture = useOuverture("fr");
  const horaires = JSON.stringify(horairesSchemaOrg(ouverture.reglages, ouverture.jour));
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const serializedSchemas = schemas.map((schema) =>
    insererPhrases(JSON.stringify(schema), ouverture.phrases)
      .split(`"${HORAIRES}"`).join(horaires)
      .replace(/</g, "\\u003c"),
  );

  // ── Génération des balises hreflang ──────────────────────────────────────
  // Stratégie "home" : routes séparées /en /de /nl
  const homeHreflang = hreflang === "home" ? [
    { lang: "fr",       href: `${BASE_URL}/` },
    { lang: "en",       href: `${BASE_URL}/en` },
    { lang: "de",       href: `${BASE_URL}/de` },
    { lang: "nl",       href: `${BASE_URL}/nl` },
    { lang: "x-default", href: `${BASE_URL}/` },
  ] : [];

  // Stratégie "minisite" : URLs distinctes par langue via ?lang= pour signaler les variantes à Google
  const minisiteHreflang = hreflang === "minisite" ? [
    { lang: "fr",        href: `${url}?lang=fr` },
    { lang: "en",        href: `${url}?lang=en` },
    { lang: "de",        href: `${url}?lang=de` },
    { lang: "nl",        href: `${url}?lang=nl` },
    { lang: "x-default", href: url },
  ] : [];

  const hreflangLinks = hreflang === "home" ? homeHreflang : minisiteHreflang;

  return (
    <>
    <Helmet>
      {/* Basiques */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      <link rel="describedby" href={`${BASE_URL}/llms.txt`} />
      {markdownUrl && <link rel="alternate" type="text/markdown" href={markdownUrl} />}
      {shouldPreloadHero && <link rel="preload" as="image" href={image} imageSrcSet={heroSrcSet} imageSizes={heroSizes} fetchPriority="high" />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* hreflang — versions linguistiques */}
      {hreflangLinks.map(({ lang, href }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={href} />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageAbsolue} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="fr_FR" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageAbsolue} />

    </Helmet>
    <JsonLdScripts schemas={serializedSchemas} />
    </>
  );
}

// ── Adresse commune ──────────────────────────────────────────────────────────
const ADDRESS = {
  "@type": "PostalAddress",
  "streetAddress": "Château de Syam",
  "addressLocality": "Syam",
  "postalCode": "39300",
  "addressCountry": "FR",
  "addressRegion": "Bourgogne-Franche-Comté"
};

const GEO = {
  "@type": "GeoCoordinates",
  "latitude": 46.6833,
  "longitude": 5.9167
};

// ── Organisation / TouristAttraction principale ──────────────────────────────
export const SCHEMA_ORGANIZATION = {
  "@context": "https://schema.org",
  "@type": ["TouristAttraction", "LocalBusiness"],
  "@id": `${BASE_URL}/#organization`,
  "name": "Château de Syam — Villa Palladienne",
  "alternateName": ["Villa Paladienne", "Château de Syam", "Villa Palladienne Jura"],
  "url": BASE_URL,
  "description": "Unique villa néo-palladienne de France classée Monument Historique. Visites guidées, chambres d'hôtes de charme, gîtes, mariages et événements d'entreprise dans le Jura (39).",
  "address": ADDRESS,
  "geo": GEO,
  "telephone": "+33384516414",
  "email": "contact@chateaudesyam.fr",
  "image": [
    `${BASE_URL}/media/Visiter_Header_TEST_694686b0_opt_3e4d6dc8.webp`,
    `${BASE_URL}/media/jack_varlet_070320203_juillet_syam-499_58d1c0eb_opt_3c06a787.webp`,
  ],
  "openingHoursSpecification": HORAIRES,
  "priceRange": "€€",
  "hasMap": "https://maps.google.com/?q=Chateau+de+Syam+39300",
  "tourBookingPage": `${BASE_URL}/visiter`,
  "sameAs": [
    "https://www.facebook.com/chateaudesyam",
    "https://www.instagram.com/chateaudesyam",
    "https://www.wikidata.org/wiki/Q2970624"
  ],
  "containedInPlace": {
    "@type": "AdministrativeArea",
    "name": "Jura",
    "containedInPlace": {
      "@type": "AdministrativeArea",
      "name": "Bourgogne-Franche-Comté"
    }
  }
};

// ── BreadcrumbList helper ────────────────────────────────────────────────────
export function schemaBreadcrumb(items: { name: string; path: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": item.name,
      "item": `${BASE_URL}${item.path}`
    }))
  };
}

// ── FAQ helper ───────────────────────────────────────────────────────────────
export function schemaFAQ(questions: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": questions.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a }
    }))
  };
}

// ── Visites guidées ──────────────────────────────────────────────────────────
export const SCHEMA_VISIT = {
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  "@id": `${BASE_URL}/visiter#attraction`,
  "name": "Visites guidées du Château de Syam — Villa Palladienne",
  "alternateName": "Château à visiter dans le Jura",
  "url": `${BASE_URL}/visiter`,
  "description": "Visitez la Villa Palladienne, unique château néo-palladien de France, classé Monument Historique. Visites guidées {jours_heures}{fermes_paren}, {periode_complete}. Tarif adulte 12 €, enfant 5 €.",
  "address": ADDRESS,
  "geo": GEO,
  "telephone": "+33384516414",
  "image": `${BASE_URL}/media/Visiter_Header_TEST_694686b0_opt_3e4d6dc8.webp`,
  "openingHoursSpecification": HORAIRES,
  "offers": [
    { "@type": "Offer", "name": "Adulte", "price": "12", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" },
    { "@type": "Offer", "name": "Enfant (moins de 14 ans)", "price": "5", "priceCurrency": "EUR", "availability": "https://schema.org/InStock" }
  ],
  "touristType": ["Famille", "Couple", "Groupe", "Scolaire"],
  "isAccessibleForFree": false,
  "publicAccess": true
};

export const SCHEMA_VISIT_FAQ = schemaFAQ([
  { q: "Quels sont les horaires de visite du Château de Syam ?", a: "Le Château de Syam est ouvert {jours_heures}, {periode_complete}.{fermes_phrase}" },
  { q: "Quel est le tarif d'entrée pour visiter le Château de Syam ?", a: "L'entrée coûte 12 € pour les adultes et 5 € pour les enfants de moins de 14 ans. Les enfants de 14 ans et plus sont au tarif adulte." },
  { q: "Peut-on visiter le Château de Syam en groupe ?", a: "Oui, le Château de Syam accueille les groupes touristiques, scolaires et professionnels sur réservation. Des visites guidées sur mesure sont proposées." },
  { q: "Le Château de Syam est-il classé Monument Historique ?", a: "Oui, la Villa Palladienne de Syam est classée Monument Historique. C'est l'unique château néo-palladien de France, construit au XIXe siècle." },
  { q: "Comment accéder au Château de Syam ?", a: "Le Château de Syam est situé à Syam (39300), dans le Jura, à 45 min de Lons-le-Saunier et 1h de Besançon. Parking gratuit sur place." }
]);

// ── Hébergement ──────────────────────────────────────────────────────────────
export const SCHEMA_LODGING = {
  "@context": "https://schema.org",
  "@type": "BedAndBreakfast",
  "@id": `${BASE_URL}/dormir#lodging`,
  "name": "Chambres d'hôtes du Château de Syam",
  "alternateName": "Nuit dans un château Jura",
  "url": `${BASE_URL}/dormir`,
  "description": "Séjournez dans les chambres d'hôtes du Château de Syam, demeure néo-palladienne classée Monument Historique dans le Jura. 4 chambres d'exception avec petit-déjeuner inclus, parc de 10 ha.",
  "address": ADDRESS,
  "geo": GEO,
  "telephone": "+33384516414",
  "image": `${BASE_URL}/media/Visiter_Header_TEST_694686b0_opt_3e4d6dc8.webp`,
  "priceRange": "€€€",
  "starRating": { "@type": "Rating", "ratingValue": "4" },
  "numberOfRooms": 4,
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Petit-déjeuner inclus", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parc privatif 10 ha", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Wifi gratuit", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Monument Historique classé", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parking gratuit", "value": true }
  ],
  "checkinTime": "15:00",
  "checkoutTime": "11:00"
};

export const SCHEMA_LODGING_FAQ = schemaFAQ([
  { q: "Peut-on dormir dans un château dans le Jura ?", a: "Oui, le Château de Syam propose 4 chambres d'hôtes de charme dans une demeure néo-palladienne classée Monument Historique, avec petit-déjeuner inclus et parc de 10 hectares." },
  { q: "Quelles chambres sont disponibles au Château de Syam ?", a: "Le Château de Syam propose 4 chambres d'hôtes : Carnot, Héléna, Syamour et Prestige, toutes décorées avec des meubles d'époque et du mobilier de caractère." },
  { q: "Le petit-déjeuner est-il inclus dans les chambres d'hôtes ?", a: "Oui, le petit-déjeuner gourmand est inclus dans toutes les chambres d'hôtes du Château de Syam." }
]);

// ── Gîtes ────────────────────────────────────────────────────────────────────
export const SCHEMA_GITES = {
  "@context": "https://schema.org",
  "@type": "Accommodation",
  "@id": `${BASE_URL}/gites#accommodation`,
  "name": "Gîtes César et Rosalie — Château de Syam",
  "url": `${BASE_URL}/gites`,
  "description": "Deux gîtes indépendants (César et Rosalie) au cœur du domaine du Château de Syam dans le Jura. Location de gîte en château, idéal pour les familles et les groupes.",
  "address": ADDRESS,
  "geo": GEO,
  "priceRange": "€€",
  "numberOfRooms": 2,
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Cuisine équipée", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parc privatif", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parking gratuit", "value": true }
  ]
};

export function schemaHotelRoom({
  name,
  path,
  description,
  image,
  price,
  capacity = 2,
}: {
  name: string;
  path: string;
  description: string;
  image: string;
  price: number;
  capacity?: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "HotelRoom",
    "@id": `${BASE_URL}${path}#room`,
    "name": `${name} — Château de Syam`,
    "url": `${BASE_URL}${path}`,
    "description": description,
    "image": `${BASE_URL}${image}`,
    "containedInPlace": { "@id": `${BASE_URL}/dormir#lodging` },
    "occupancy": { "@type": "QuantitativeValue", "maxValue": capacity },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": `${BASE_URL}${path}`,
    },
  };
}

export function schemaVacationRental({
  name,
  path,
  description,
  image,
  price,
  capacity,
  bedrooms,
}: {
  name: string;
  path: string;
  description: string;
  image: string;
  price: number;
  capacity: number;
  bedrooms: number;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "VacationRental",
    "@id": `${BASE_URL}${path}#rental`,
    "name": `${name} — Château de Syam`,
    "url": `${BASE_URL}${path}`,
    "description": description,
    "image": `${BASE_URL}${image}`,
    "address": ADDRESS,
    "geo": GEO,
    "maximumAttendeeCapacity": capacity,
    "numberOfBedrooms": bedrooms,
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": `${BASE_URL}${path}`,
    },
  };
}

export function schemaEventVenueDetails({
  eventTypes,
  mainSpace,
}: {
  eventTypes: string[];
  mainSpace: { name: string; description: string; image: string; capacity: number };
}) {
  return {
    "@context": "https://schema.org",
    "@type": "EventVenue",
    "@id": `${BASE_URL}/evenement#services`,
    "name": "Espaces événementiels du Château de Syam",
    "url": `${BASE_URL}/evenement`,
    "containsPlace": {
      "@type": "EventVenue",
      "name": mainSpace.name,
      "description": mainSpace.description,
      "image": `${BASE_URL}${mainSpace.image}`,
      "maximumAttendeeCapacity": mainSpace.capacity,
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Événements et séminaires au Château de Syam",
      "itemListElement": eventTypes.map((name) => ({
        "@type": "Offer",
        "itemOffered": { "@type": "Service", "name": name },
      })),
    },
  };
}

// ── Mariage ──────────────────────────────────────────────────────────────────
export const SCHEMA_WEDDING = {
  "@context": "https://schema.org",
  "@type": "EventVenue",
  "@id": `${BASE_URL}/mariage#venue`,
  "name": "Mariage au Château de Syam — Salle de réception Jura",
  "alternateName": "Château de mariage dans le Jura",
  "url": `${BASE_URL}/mariage`,
  "description": "Organisez votre mariage dans un cadre d'exception : château néo-palladien classé Monument Historique, salle de réception 170 m², parc de 10 ha, jusqu'à 150 convives. Jura, Bourgogne-Franche-Comté.",
  "address": ADDRESS,
  "geo": GEO,
  "telephone": "+33384516414",
  "image": `${BASE_URL}/media/jack_varlet_070320203_juillet_syam-499_58d1c0eb_opt_3c06a787.webp`,
  "maximumAttendeeCapacity": 150,
  "amenityFeature": [
    { "@type": "LocationFeatureSpecification", "name": "Salle de réception 170 m²", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Parc de 10 hectares", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Hébergement sur place", "value": true },
    { "@type": "LocationFeatureSpecification", "name": "Monument Historique classé", "value": true }
  ]
};

export const SCHEMA_WEDDING_FAQ = schemaFAQ([
  { q: "Peut-on se marier dans un château dans le Jura ?", a: "Oui, le Château de Syam accueille les mariages et réceptions dans sa salle de 170 m² et son parc de 10 hectares, pour jusqu'à 150 convives." },
  { q: "Combien de personnes peut accueillir le Château de Syam pour un mariage ?", a: "Le Château de Syam peut accueillir jusqu'à 150 convives pour un mariage ou une réception dans sa salle historique de 170 m²." },
  { q: "Le Château de Syam propose-t-il l'hébergement pour les mariages ?", a: "Oui, le Château de Syam dispose de 4 chambres d'hôtes et 2 gîtes sur le domaine, permettant aux mariés et leurs proches de séjourner sur place." }
]);

// ── Événements ───────────────────────────────────────────────────────────────
export const SCHEMA_EVENT = {
  "@context": "https://schema.org",
  "@type": "EventVenue",
  "@id": `${BASE_URL}/evenement#venue`,
  "name": "Événements d'entreprise au Château de Syam",
  "alternateName": "Séminaire château Jura",
  "url": `${BASE_URL}/evenement`,
  "description": "Séminaires, galas, lancements de produits et événements d'entreprise dans un cadre historique exceptionnel. Château néo-palladien classé, espaces modulables jusqu'à 200 personnes. Jura.",
  "address": ADDRESS,
  "telephone": "+33384516414",
  "image": `${BASE_URL}/media/jack_varlet_070320203_juillet_syam-499_58d1c0eb_opt_3c06a787.webp`,
  "maximumAttendeeCapacity": 200
};

// ── Roseraie ─────────────────────────────────────────────────────────────────
export const SCHEMA_ROSERAIE = {
  "@context": "https://schema.org",
  "@type": "Park",
  "@id": `${BASE_URL}/roseraie#park`,
  "name": "Roseraie du Château de Syam",
  "url": `${BASE_URL}/roseraie`,
  "description": "Roseraie historique du Château de Syam, plus de 200 variétés de roses dans un parc de 10 hectares classé Monument Historique. À voir dans le Jura.",
  "address": ADDRESS,
  "image": `${BASE_URL}/media/jack_varlet_05292020syam_29_mai-421_bbc2cc00_opt_5a6f1dd3.webp`
};

// ── Groupe ───────────────────────────────────────────────────────────────────
export const SCHEMA_GROUPE = {
  "@context": "https://schema.org",
  "@type": "TouristAttraction",
  "@id": `${BASE_URL}/groupe#attraction`,
  "name": "Visites de groupe au Château de Syam",
  "url": `${BASE_URL}/groupe`,
  "description": "Accueil de groupes touristiques, scolaires et professionnels au Château de Syam. Visites guidées sur mesure, repas de groupe, séminaires dans le Jura.",
  "address": ADDRESS,
  "telephone": "+33384516414",
  "touristType": ["Groupe", "Scolaire", "Professionnel"]
};

// ── Offrir ───────────────────────────────────────────────────────────────────
export const SCHEMA_OFFRIR = {
  "@context": "https://schema.org",
  "@type": "Product",
  "@id": `${BASE_URL}/offrir#product`,
  "name": "Formules cadeaux — Château de Syam",
  "url": `${BASE_URL}/offrir`,
  "description": "Offrez une expérience unique au Château de Syam : formule cadeau visite, séjour, repas ou événement dans un monument historique du Jura.",
  "brand": { "@type": "Brand", "name": "Château de Syam" },
  "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "EUR" }
};

// ── Page fantôme : Château dans le Jura ─────────────────────────────────────
export const SCHEMA_CHATEAU_JURA = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Château de Syam : le plus beau château à visiter dans le Jura",
  "description": "Découvrez le Château de Syam, unique villa néo-palladienne de France classée Monument Historique, situé à Syam dans le Jura (39). Visites guidées, hébergement, mariage.",
  "url": `${BASE_URL}/chateau-jura`,
  "author": { "@type": "Organization", "name": "Château de Syam" },
  "publisher": { "@type": "Organization", "name": "Château de Syam", "url": BASE_URL },
  "about": SCHEMA_ORGANIZATION,
  "mainEntityOfPage": `${BASE_URL}/chateau-jura`
};
